// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '@loa/theme';
import { AppIcon, DropdownSearchInput, SearchBar, TablePagination } from '@loa/ui-components';
import {
  useRequestOverview,
  useRequestOverviewFilterOptions,
} from '../../hooks/useRequestOverview';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type {
  ProfileRequestOverviewDTO,
  RequestOverviewDisplayStatus,
} from '../../types/requestOverview';
import { RequestDisplayStatusBadge, displayStatusLabel } from '../requests/RequestDisplayStatusBadge';
import './RequestOverviewCard.css';

/**
 * Stable label lookup for the request_type discriminator. New backend
 * handlers can add new typeName() values without UI changes — the table
 * falls back to a humanised SNAKE_CASE → Title Case render for unknown
 * types via {@link humaniseRequestType}.
 */
const REQUEST_TYPE_LABELS: Record<string, string> = {
  LOA: 'LoA',
  COA: 'CoA',
  OPEN_BANKING_CONNECTION: 'Open Banking',
  RETIREMENT_OPTIONS: 'Retirement options',
  LEAVE_OF_SERVICE_OPTIONS: 'Leave of service',
  KYC_VERIFICATION: 'KYC',
  FACT_FIND: 'Fact find',
  ANNUAL_REVIEW: 'Annual review',
  AML_DOCUMENTATION: 'AML',
};

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const words = name.split(' ').filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function humaniseRequestType(type: string): string {
  if (REQUEST_TYPE_LABELS[type]) return REQUEST_TYPE_LABELS[type];
  // Fallback: convert SNAKE_CASE → Title Case
  return type
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

function profileTypeLabel(profileType: string | null | undefined): string {
  if (!profileType) return '-';
  switch (profileType.toUpperCase()) {
    case 'SOLO': return 'Solo';
    case 'JOINT': return 'Joint';
    default: return profileType.charAt(0) + profileType.slice(1).toLowerCase();
  }
}

// ============================================
// Filter dropdown popover
// ============================================

interface FilterDropdownProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  /**
   * When true the popover renders a {@link DropdownSearchInput} above the
   * checkbox list. The consumer is responsible for the controlled
   * value/onChange and for triggering the actual server-side refetch.
   */
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (next: string) => void;
  searchPlaceholder?: string;
}

function FilterDropdown({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Recompute the popover position whenever it opens, on resize, and on
  // ancestor scroll. Without scroll re-positioning the popover detaches
  // from its trigger when the page scrolls.
  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({ top: rect.bottom + 4, left: rect.left });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(t) &&
        triggerRef.current &&
        !triggerRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleOption = (value: string) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  const triggerClass =
    'request-overview__filter-trigger' +
    (selected.length > 0 ? ' request-overview__filter-trigger--active' : '');

  const labelKebab = label.toLowerCase().replace(/\s+/g, '-');
  // When the user is actively searching the empty-state text changes from
  // "No options" to "No matches" to match the user's mental model.
  const emptyMessage = searchable && searchValue.trim().length > 0 ? 'No matches' : 'No options';

  const popover = open && position ? createPortal(
    <div
      ref={popoverRef}
      className="request-overview__filter-popover"
      role="listbox"
      style={{ top: position.top, left: position.left }}
    >
      {searchable && (
        <div className="request-overview__filter-search">
          <DropdownSearchInput
            value={searchValue}
            onChange={(next) => onSearchChange?.(next)}
            placeholder={searchPlaceholder ?? `Search ${label.toLowerCase()}`}
            ariaLabel={`Search ${label}`}
            data-testid={`filter-search-${labelKebab}`}
          />
        </div>
      )}
      <div className="request-overview__filter-options">
        {options.length === 0 ? (
          <span className="request-overview__filter-empty">{emptyMessage}</span>
        ) : (
          options.map((opt) => (
            <label
              key={opt.value}
              className="request-overview__filter-option"
              data-testid={`filter-option-${labelKebab}-${opt.value}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))
        )}
      </div>
      {selected.length > 0 && (
        <button
          type="button"
          className="request-overview__filter-clear"
          onClick={() => onChange([])}
        >
          Clear
        </button>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <div className="request-overview__filter-cell">
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Filter by ${label}`}
      >
        {label}
        {selected.length > 0 && ` (${selected.length})`}
        <AppIcon name="chevron down" size={14} />
      </button>
      {popover}
    </div>
  );
}

// ============================================
// Main card
// ============================================

export function RequestOverviewCard() {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [providerIds, setProviderIds] = useState<string[]>([]);
  const [policyTypeIds, setPolicyTypeIds] = useState<string[]>([]);
  const [requestTypes, setRequestTypes] = useState<string[]>([]);
  const [displayStatuses, setDisplayStatuses] = useState<RequestOverviewDisplayStatus[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    providers,
    policyTypes,
    requestTypes: requestTypeOptionsFromApi,
    displayStatuses: displayStatusOptionsFromApi,
    loading: filterOptionsLoading,
    refetch: refetchFilterOptions,
  } = useRequestOverviewFilterOptions(false);

  // Per-filter search input (live text typed by the user) + the debounced
  // value the network layer actually fires on. Status has no search input.
  const [providerSearch, setProviderSearch] = useState('');
  const [policyTypeSearch, setPolicyTypeSearch] = useState('');
  const [requestTypeSearch, setRequestTypeSearch] = useState('');

  const debouncedProviderSearch = useDebouncedValue(providerSearch, 300);
  const debouncedPolicyTypeSearch = useDebouncedValue(policyTypeSearch, 300);
  const debouncedRequestTypeSearch = useDebouncedValue(requestTypeSearch, 300);

  // Skip the very first effect run (initial load already covered "no
  // search"); subsequent runs hit the server with whatever the user typed.
  const isFirstSearchRun = useRef({ providers: true, policyTypes: true, requestTypes: true });

  useEffect(() => {
    if (isFirstSearchRun.current.providers) {
      isFirstSearchRun.current.providers = false;
      return;
    }
    refetchFilterOptions('providers', debouncedProviderSearch);
  }, [debouncedProviderSearch, refetchFilterOptions]);

  useEffect(() => {
    if (isFirstSearchRun.current.policyTypes) {
      isFirstSearchRun.current.policyTypes = false;
      return;
    }
    refetchFilterOptions('policyTypes', debouncedPolicyTypeSearch);
  }, [debouncedPolicyTypeSearch, refetchFilterOptions]);

  useEffect(() => {
    if (isFirstSearchRun.current.requestTypes) {
      isFirstSearchRun.current.requestTypes = false;
      return;
    }
    refetchFilterOptions('requestTypes', debouncedRequestTypeSearch);
  }, [debouncedRequestTypeSearch, refetchFilterOptions]);

  // Capture the option count from the FIRST non-loading render — the search
  // input is shown only when this initial count was >= 3. We freeze it once
  // so the input does NOT disappear when the user filters down to <3 hits.
  const [initialCounts, setInitialCounts] = useState<{
    providers: number | null;
    policyTypes: number | null;
    requestTypes: number | null;
  }>({ providers: null, policyTypes: null, requestTypes: null });

  useEffect(() => {
    if (filterOptionsLoading) return;
    setInitialCounts((prev) => ({
      providers: prev.providers ?? providers.length,
      policyTypes: prev.policyTypes ?? policyTypes.length,
      requestTypes: prev.requestTypes ?? requestTypeOptionsFromApi.length,
    }));
  }, [filterOptionsLoading, providers.length, policyTypes.length, requestTypeOptionsFromApi.length]);

  const providerSearchable = (initialCounts.providers ?? 0) >= 3;
  const policyTypeSearchable = (initialCounts.policyTypes ?? 0) >= 3;
  const requestTypeSearchable = (initialCounts.requestTypes ?? 0) >= 3;

  const { data, loading, error, featureDisabled } = useRequestOverview({
    search: committedSearch || undefined,
    requestType: requestTypes,
    displayStatus: displayStatuses,
    providerId: providerIds,
    policyTypeId: policyTypeIds,
    page,
    size: pageSize,
  });

  // Any filter / search change resets to page 0 so the user doesn't land on
  // an empty page after narrowing the result set.
  useEffect(() => {
    setPage(0);
  }, [committedSearch, providerIds, policyTypeIds, requestTypes, displayStatuses, pageSize]);

  const handleSubmitSearch = useCallback(() => {
    setCommittedSearch(searchInput.trim());
  }, [searchInput]);

  const handleRowClick = useCallback(
    (row: ProfileRequestOverviewDTO) => {
      navigate(`/relationships/${row.profileId}`);
    },
    [navigate],
  );

  const providerOptions = useMemo(
    () => providers.map((p) => ({ value: p.id, label: p.name })),
    [providers],
  );

  const policyTypeOptions = useMemo(
    () => policyTypes.map((p) => ({ value: p.id, label: p.name })),
    [policyTypes],
  );

  const requestTypeOptions = useMemo(
    () => requestTypeOptionsFromApi.map((t) => ({ value: t, label: humaniseRequestType(t) })),
    [requestTypeOptionsFromApi],
  );

  const statusOptions = useMemo(
    () => displayStatusOptionsFromApi.map((s) => ({ value: s, label: displayStatusLabel(s) })),
    [displayStatusOptionsFromApi],
  );

  // Feature flag off → hide silently (matches PendingRequestsCard's behaviour).
  if (featureDisabled) return null;

  return (
    <div className="request-overview" data-testid="request-overview-card">
      <div className="request-overview__header">
        <h2 className="request-overview__title">Request Overview</h2>
      </div>

      <div className="request-overview__search">
        <div className="request-overview__search-bar">
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            onSearch={handleSubmitSearch}
            placeholder="Search by relationship name, provider or policy type"
          />
        </div>
      </div>

      {error && (
        <div className="request-overview__error" role="alert">
          {error}
        </div>
      )}

      {(() => {
        if (loading && !data) {
          return (
            <table className="request-overview__table">
              <tbody>
                {Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="request-overview__skeleton-row">
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j}><div className="request-overview__skeleton-bar" /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }

        if (!data || data.content.length === 0) {
          return (
            <div className="request-overview__empty" data-testid="request-overview-empty">
              <span>No requests match these filters</span>
            </div>
          );
        }

        return (
          <>
            <table className="request-overview__table">
              <thead>
                <tr>
                  <th>
                    <FilterDropdown
                      label="Provider"
                      options={providerOptions}
                      selected={providerIds}
                      onChange={setProviderIds}
                      searchable={providerSearchable}
                      searchValue={providerSearch}
                      onSearchChange={setProviderSearch}
                      searchPlaceholder="Search providers"
                    />
                  </th>
                  <th>Client</th>
                  <th>
                    <FilterDropdown
                      label="Policy Type"
                      options={policyTypeOptions}
                      selected={policyTypeIds}
                      onChange={setPolicyTypeIds}
                      searchable={policyTypeSearchable}
                      searchValue={policyTypeSearch}
                      onSearchChange={setPolicyTypeSearch}
                      searchPlaceholder="Search policy types"
                    />
                  </th>
                  <th>Type</th>
                  <th>Last updated</th>
                  <th>
                    <FilterDropdown
                      label="Request Type"
                      options={requestTypeOptions}
                      selected={requestTypes}
                      onChange={setRequestTypes}
                      searchable={requestTypeSearchable}
                      searchValue={requestTypeSearch}
                      onSearchChange={setRequestTypeSearch}
                      searchPlaceholder="Search by code (e.g., LOA)"
                    />
                  </th>
                  <th>
                    <FilterDropdown
                      label="Status"
                      options={statusOptions}
                      selected={displayStatuses}
                      onChange={(next) => setDisplayStatuses(next as RequestOverviewDisplayStatus[])}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.content.map((row) => (
                  <tr
                    key={row.requestId}
                    className="request-overview__row"
                    onClick={() => handleRowClick(row)}
                    data-testid={`request-overview-row-${row.requestId}`}
                  >
                    <td>
                      <div className="request-overview__provider">
                        <div className="request-overview__provider-avatar">
                          {row.providerLogoUrl ? (
                            <img src={row.providerLogoUrl} alt={row.providerName ?? 'Provider'} />
                          ) : (
                            getInitials(row.providerName)
                          )}
                        </div>
                        <span className="request-overview__provider-name">
                          {row.providerName ?? '-'}
                        </span>
                      </div>
                    </td>
                    <td>{row.profileName || '-'}</td>
                    <td>{row.policyTypeName ?? '-'}</td>
                    <td>{profileTypeLabel(row.profileType)}</td>
                    <td>{formatDateTime(row.lastUpdated)}</td>
                    <td>
                      <span className="request-overview__type-pill">
                        {humaniseRequestType(row.requestType)}
                      </span>
                    </td>
                    <td>
                      <RequestDisplayStatusBadge status={row.displayStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {data.totalPages > 0 && (
              <TablePagination
                config={{
                  currentPage: page + 1,
                  totalPages: data.totalPages,
                  itemsPerPage: pageSize,
                  itemsPerPageOptions: [5, 10, 20],
                  onPageChange: (next) => setPage(next - 1),
                  onItemsPerPageChange: (size) => {
                    setPageSize(size);
                    setPage(0);
                  },
                }}
              />
            )}
          </>
        );
      })()}
    </div>
  );
}

export default RequestOverviewCard;
